
import { defineComponent, ref, Ref, PropType, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { IConfig } from '../type.d';
import {
  IAppManageInfoItem,
} from '@/api/manage-app/app-manage/index.d';
import {
  APP_UPDATE_LEVEL_LIST,
  ADAPTATION_SYSTEM_ENUM,
  UPDATE_DIMENSION_ENUM,
} from '@/constant';
import {
  getAppConfigLatest,
  getOrganizationInfosTree,
  postWebV1UpdateConfigApi,
} from '@/api/manage-app/app-update-config';
import Panel from './panel/index.vue';
import usePermissionConfig from '../use-permission-config';

export default defineComponent({
  components: {
    Panel,
  },
  props: {
    // 类型
    type: {
      type: String,
      required: true,
    },
    // app
    app: {
      type: Object as PropType<IAppManageInfoItem>,
      required: true,
    },
  },
  setup(props) {
    // iOS 线上版本的配置
    const iOSOnline: Ref<IConfig> = ref({
      id: '',
      configKey: '',
      configValue: {
        updateLevel: '',
        latestVersion: '',
        downloadUrl: '',
        newDownloadUrl: '',
        changelog: '',
        departmentIds: [],
        specifyVersion: '',
      },
    });
    // iOS 下一版本的配置
    const iOSNext: Ref<IConfig> = ref({
      id: '',
      configKey: '',
      configValue: {
        updateLevel: '',
        latestVersion: '',
        downloadUrl: '',
        newDownloadUrl: '',
        changelog: '',
        departmentIds: [],
        specifyVersion: '',
      },
    });
    // android 线上版本的配置
    const androidOnline: Ref<IConfig> = ref({
      id: '',
      configKey: '',
      configValue: {
        updateLevel: '',
        latestVersion: '',
        downloadUrl: '',
        newDownloadUrl: '',
        changelog: '',
        departmentIds: [],
        specifyVersion: '',
      },
    });
    // android 下一版本的配置
    const androidNext: Ref<IConfig> = ref({
      id: '',
      configKey: '',
      configValue: {
        updateLevel: '',
        latestVersion: '',
        downloadUrl: '',
        newDownloadUrl: '',
        changelog: '',
        departmentIds: [],
        specifyVersion: '',
      },
    });
    const organizationTree: Ref<any[]> = ref([]);
    const iOSForm: Ref<any | null> = ref(null);
    const androidForm: Ref<any | null> = ref(null);
    const androidTree: Ref<any | null> = ref(null);
    const iOSTree: Ref<any | null> = ref(null);
    const updateDepartmentDimension = computed(() => {
      return props.app.updateDimension === UPDATE_DIMENSION_ENUM.DEPARTMENT;
    });
    const getOnlineVersionAndDownloadUrl = async () => {
      const { data: iosData } = await getAppConfigLatest({
        appClient: props.app.appCode,
        appPlatform: 'ios',
      });
      const { data: androidData } = await getAppConfigLatest({
        appClient: props.app.appCode,
        appPlatform: 'android',
      });
      // 获取线上版本和下载地址
      iOSOnline.value.configValue.latestVersion = iosData.versionNum || '';
      iOSNext.value.configValue.newDownloadUrl = props.app.iosAddress || '';
      androidOnline.value.configValue.latestVersion = androidData.versionNum || '';
      androidNext.value.configValue.newDownloadUrl = props.app.androidAddress || '';
    };
    getOnlineVersionAndDownloadUrl();
    // 获取部门树
    const getOrganizationTree = async () => {
      const { data } = await getOrganizationInfosTree();
      const { organizationTreeVos } = data || {};
      organizationTree.value = organizationTreeVos ?? [];
    };
    if (updateDepartmentDimension.value) {
      getOrganizationTree();
    }
    // 更新级别的描述（弹窗信息）
    const getUpdateLevelDesc = (val: string) => {
      const updateLevel = APP_UPDATE_LEVEL_LIST.find(item => item.value === val);
      return updateLevel?.label || '-';
    };
    const name = ref<string>(''); // 记录单个选中的组织名称
    const findOrganizationName = (id: string, arr: any) => {
      arr.forEach((it: any) => {
        if (!(it.organizationId as string === id)) {
          findOrganizationName(id, it.children);
        } else {
          name.value += `${it.organizationName}-`;
        }
      });
    };
    const getNewTreeCheckedNodes = (treeName: 'androidTree' | 'iOSTree') => {
      const withoutChildrenCheckedNodes = treeName === 'androidTree'
        ? androidTree.value.getCheckedNodes()
        : iOSTree.value.getCheckedNodes();
      return withoutChildrenCheckedNodes.map((it: any) => {
        const organizationRoutes = (it.organizationIdRoute || []).split(',');
        organizationRoutes.shift();
        name.value = '';
        organizationRoutes.forEach((id: string) => {
          findOrganizationName(id, organizationTree.value);
        });
        name.value = name.value.substr(0, name.value.length - 1);
        return {
          deptNames: name.value,
          deptCodes: it.organizationId,
        };
      });
    };
    const submitForm = async (formName: 'iOSForm' | 'androidForm') => {
      try {
        if (formName === 'iOSForm') {
          await iOSForm.value.validate();
        } else {
          await androidForm.value.validate();
        }
        // const { app } = this;
        const isIOS = formName === 'iOSForm';
        // const platformName = isIOS ? 'iOS' : 'Android';
        // const onlineConfig = isIOS ? iOSOnline.value : androidOnline.value;
        let newDepartments;
        if (updateDepartmentDimension.value) {
          const treeName = isIOS ? 'iOSTree' : 'androidTree';
          newDepartments = getNewTreeCheckedNodes(treeName);
          if (newDepartments.length === 0) {
            ElMessage.warning('请选择更新部门');
            return;
          }
        }
        const data = isIOS ? iOSNext.value.configValue : androidNext.value.configValue;
        // console.log(postWebV1UpdateConfigApi, data);
        await postWebV1UpdateConfigApi({
          appName: props.app.appName,
          appPlatform: isIOS ? 'ios' : 'android',
          appClient: props.app.appCode,
          updateLevel: data.updateLevel as 'force' | 'optional',
          versionNum: data.latestVersion,
          // deptCodes: newDepartments.map((item: any) => item.organizationId).join(','),
          updateConfigDeptInfos: newDepartments,
          installAddress: data.newDownloadUrl,
          updateLog: data.changelog,
          specifyVersion: data.specifyVersion,
        });
        ElMessage.success('操作成功');
        getOnlineVersionAndDownloadUrl();
      } catch (e) {
        console.log(e);
      }
    };
    const handleVersions = (rule: any, value: any, callback: (err?: Error) => any, multi = false) => {
      if (multi) {
        value = value.split(',');
      } else {
        value = [value];
      }
      value.forEach((version: string) => {
        if (version && !/^[0-9]\d?(\.(0|[1-9]\d?)){2}$/.test(version)) {
          callback(new Error('请输入正确的版本号格式'));
        }
      });
      callback();
    };

    const permissionConfig = usePermissionConfig();

    return {
      handleVersions,
      iOSForm,
      androidForm,
      iOSTree,
      androidTree,
      iOSOnline,
      iOSNext,
      androidOnline,
      androidNext,
      organizationTree,
      submitForm,
      defaultProps: {
        children: 'children',
        label: 'organizationName',
      },
      APP_UPDATE_LEVEL_LIST,
      ADAPTATION_SYSTEM_ENUM,
      UPDATE_DIMENSION_ENUM,
      getOrganizationTree,
      getOnlineVersionAndDownloadUrl,
      getUpdateLevelDesc,
      permissionConfig,
    };
  },
});
